import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'

export interface OverridableBooleanParam {
  /** Effective value: override ?? snapshotBase ?? defaultValue. */
  value: boolean
  /** User-edit setter. */
  setValue: Dispatch<SetStateAction<boolean>>
}

/**
 * Boolean twin of `useOverridableParam`: a snapshot-prefilled toggle that stays
 * user-editable. The override is stored separately from `snapshotBase`, so a
 * late-arriving/changing snapshot needs no sync effect, and a user edit keeps
 * winning over a later snapshot change. Resets when `componentId` changes.
 */
export function useOverridableBooleanParam(
  snapshotBase: boolean | undefined,
  defaultValue: boolean,
  componentId: string | undefined
): OverridableBooleanParam {
  const [override, setOverride] = useState<boolean | null>(null)

  useEffect(() => {
    setOverride(null)
  }, [componentId])

  const value = override ?? snapshotBase ?? defaultValue

  const setValue: Dispatch<SetStateAction<boolean>> = useCallback(
    (action) =>
      setOverride((prev) => {
        const prevValue = prev ?? snapshotBase ?? defaultValue
        return typeof action === 'function'
          ? (action as (p: boolean) => boolean)(prevValue)
          : action
      }),
    [snapshotBase, defaultValue]
  )

  return useMemo(() => ({ value, setValue }), [value, setValue])
}
